import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/components/common/PageHeader";
import { impersonationService } from "@/services/impersonation.service";

function ImpersonationLogPage({ embedded = false }: { embedded?: boolean }) {
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-impersonation-logs", page],
    queryFn: () => impersonationService.listLogs({ page, limit }),
  });

  return (
    <div className="space-y-6">
      {!embedded && (
        <PageHeader
          title="Impersonation logs"
          subtitle="Track all admin impersonation sessions."
        />
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
            <tr>
              <th className="px-4 py-3">Admin</th>
              <th className="px-4 py-3">Tenant</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Reason</th>
              <th className="px-4 py-3">Started</th>
              <th className="px-4 py-3">Ended</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={6}>
                  Loading logs...
                </td>
              </tr>
            ) : data?.data.length ? (
              data.data.map((log) => (
                <tr
                  key={log.id}
                  className="border-b border-slate-100 last:border-none"
                >
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.admin?.email || log.adminId}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.tenant?.name || log.tenantId}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.userId}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.reason || "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {new Date(log.startTime).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.endTime ? new Date(log.endTime).toLocaleString() : "—"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={6}>
                  No impersonation logs.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>
          Page {data?.meta.page ?? page} of {data?.meta.totalPages ?? 1}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={Boolean(data) && page >= (data?.meta.totalPages ?? 1)}
            onClick={() => setPage((value) => value + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImpersonationLogPage;
